import React from 'react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useClientSitePreferences} from '@/context/site-preferences'
import {Form, FormField, FormInput} from '@/components/ui/form'
import {Button} from '@/components/ui/button'
import {zodResolver} from '@hookform/resolvers/zod'
import {useForm} from 'react-hook-form'
import {z} from 'zod'

import {useShopperLoginMutation} from '@salesforce/commerce-sdk-react'

interface ForgotPasswordFormProps {
    onSuccess?: () => void
    onEmailTypeChange?: (type: 'reset-password' | 'passwordless') => void
    onPasswordlessRateLimit?: () => void
}

type ResetPasswordRequest = {
    user_id: string
    mode: string
    channel_id: string
    callback_uri: string
    hint: string
}

const ForgotPasswordForm = ({
    onSuccess,
    onEmailTypeChange,
    onPasswordlessRateLimit
}: ForgotPasswordFormProps) => {
    const {site} = useMultiSite()
    const siteId: string = site.id
    const prefs = useClientSitePreferences()
    const enableMagicLink = prefs['MagicLink'].enableMagicLink

    const FormSchema = z.object({
        email: z
            .string({
                required_error: 'This field is required'
            })
            .min(1, 'This field is required')
            .email({message: 'Please enter a valid email address'})
    })

    const form = useForm<{email: string}>({
        resolver: zodResolver(FormSchema),
        defaultValues: {
            email: ''
        }
    })

    const getResetPasswordTokenLogin = useShopperLoginMutation('getPasswordResetToken')

    const setGenericEmailError = (flow: 'passwordless' | 'reset-password', error: unknown) => {
        console.error(`[${flow}]`, error)
        form.setError('email', {
            type: 'manual',
            message: 'Something went wrong, please try again later.'
        })
    }

    const handlePasswordlessLogin = async ({email}: {email: string}) => {
        form.clearErrors('email')
        onEmailTypeChange?.('passwordless')

        try {
            const response = await fetch('/api/passwordless/request', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({email, channel_id: siteId})
            })

            if (response.status === 429) {
                onPasswordlessRateLimit?.()
                return
            }

            if (!response.ok) {
                throw new Error(`Request failed: ${response.status}`)
            }

            onSuccess?.()
        } catch (error) {
            setGenericEmailError('passwordless', error)
        }
    }

    const handleResetPassword = async ({email}: {email: string}) => {
        form.clearErrors('email')
        onEmailTypeChange?.('reset-password')

        const body: ResetPasswordRequest = {
            user_id: email,
            mode: 'callback',
            channel_id: siteId,
            hint: 'cross_device',
            callback_uri: `${window.location.origin}/api/password-reset`
        }

        try {
            await getResetPasswordTokenLogin.mutateAsync({body})
            onSuccess?.()
        } catch (error) {
            setGenericEmailError('reset-password', error)
        }
    }

    return (
        <Form {...form} data-testid="forgotPasswordForm">
            <form onSubmit={form.handleSubmit(handleResetPassword)} className="space-y-6">
                <FormField
                    control={form.control}
                    name="email"
                    render={({field}) => (
                        <FormInput
                            label="Email"
                            inputType="email"
                            required
                            fieldProps={field}
                            validate={() => form.trigger('email')}
                            data-testid="forgotPasswordForm-FormInput"
                        />
                    )}
                    data-testid="forgotPasswordForm-FormField"
                />

                <div className="flex flex-col gap-2">
                    {enableMagicLink && (
                        <Button
                            type="button"
                            ctaType="button"
                            variant="primary"
                            className="w-[18.75rem] mx-auto"
                            data-testid="forgotPasswordForm-PasswordlessLoginButton"
                            onClick={form.handleSubmit(handlePasswordlessLogin)}
                        >
                            Email Sign In Link
                        </Button>
                    )}

                    <Button
                        type="submit"
                        ctaType="button"
                        variant="primary"
                        className="w-[18.75rem] mx-auto"
                        data-testid="forgotPasswordForm-Button"
                    >
                        Reset Password
                    </Button>
                </div>
            </form>
        </Form>
    )
}

export default ForgotPasswordForm
